import {
  Viewer,
  Cartesian2,
  Cartesian3,
  defined,
  Math as CesiumMath,
  Transforms,
  Matrix4,
  Matrix3,
  Quaternion,
  Ellipsoid,
  Camera,
  Ray,
  IntersectionTests,
  SceneTransforms,
} from "cesium";

export class CameraOrbitController {
  private readonly _viewer: Viewer;

  private readonly _maximumRotateRate = 1.77;
  private readonly _minimumRotateRate = 1.0 / 5000.0;

  /**
   * A parameter in the range <code>(0, 1)</code> used to limit the range
   * of various user inputs to a percentage of the window width/height per animation frame.
   * This helps keep the camera under control in low-frame-rate situations.
   * @default 0.1
   */
  maximumMovementRatio: number = 0.1;

  /**
   * The angle, relative to the ellipsoid normal, restricting the maximum amount
   * that the user can tilt the camera. If <code>undefined</code>, the angle of
   * the camera tilt is unrestricted.
   * @default undefined
   * @example
   * // Prevent the camera from tilting below the ellipsoid surface
   * viewer.scene.screenSpaceCameraController.maximumTiltAngle = Math.PI / 2.0;
   */
  maximumTiltAngle: number | undefined = undefined;

  constructor(viewer: Viewer) {
    this._viewer = viewer;
  }

  private readonly _scratchWindowPos = new Cartesian2();
  private readonly _scratchVerticalCenter = new Cartesian3();
  private readonly _scratchRay = new Ray();
  private readonly _scratchRadii = new Cartesian3();
  private readonly _scratchEllipsoid = new Ellipsoid();
  private readonly _scratchCartesian3 = new Cartesian3();
  private readonly _scratchTransform = new Matrix4();
  private readonly _scratchVerticalTransform = new Matrix4();
  private readonly _scratchOldTransform = new Matrix4();
  private readonly _scratchQuaternion = new Quaternion();
  private readonly _scratchMatrix = new Matrix3();

  /**
   * Orbit algorithm based on Cesium's built-in camera functions.
   * @link https://github.com/CesiumGS/cesium/blob/main/packages/engine/Source/Scene/ScreenSpaceCameraController.js#L2409
   */
  orbit(
    movement: { startPosition: Cartesian2; endPosition: Cartesian2 },
    pivot: Cartesian3,
  ): void {
    if (!pivot || pivot.equals(Cartesian3.ZERO)) return;

    const scene = this._viewer.scene;
    const camera = scene.camera;
    const isCameraUnderground = scene.cameraUnderground;

    //calculate a vertical pivot point
    const windowPosition = SceneTransforms.worldToWindowCoordinates(
      scene,
      pivot,
      this._scratchWindowPos,
    );
    if (
      !defined(windowPosition) ||
      windowPosition.x < 0 ||
      windowPosition.x > scene.canvas.clientWidth ||
      windowPosition.y < 0 ||
      windowPosition.y > scene.canvas.clientHeight
    ) {
      return;
    }

    windowPosition.x = scene.canvas.clientWidth / 2;
    const ray = camera.getPickRay(windowPosition, this._scratchRay);

    const mag = Cartesian3.magnitude(pivot);
    const radii = Cartesian3.fromElements(mag, mag, mag, this._scratchRadii);
    const newEllipsoid = Ellipsoid.fromCartesian3(
      radii,
      this._scratchEllipsoid,
    );

    const intersection = IntersectionTests.rayEllipsoid(ray, newEllipsoid);
    if (!defined(intersection)) {
      return;
    }

    const t =
      Cartesian3.magnitude(ray.origin) > mag
        ? intersection.start
        : intersection.stop;
    const verticalPivot = Ray.getPoint(ray, t, this._scratchVerticalCenter);

    //set up transforms for each pivot
    const transform = Transforms.eastNorthUpToFixedFrame(
      pivot,
      scene.ellipsoid,
      this._scratchTransform,
    );
    const verticalTransform = Transforms.eastNorthUpToFixedFrame(
      verticalPivot,
      newEllipsoid,
      this._scratchVerticalTransform,
    );

    //perform the vertical orbit rotation first
    let constrainedAxis = Cartesian3.UNIT_Z;

    const oldTransform = Matrix4.clone(
      camera.transform,
      this._scratchOldTransform,
    );
    this.setTransform(camera, verticalTransform);

    const tangent = Cartesian3.cross(
      verticalPivot,
      camera.positionWC,
      this._scratchCartesian3,
    );
    const dot = Cartesian3.dot(camera.rightWC, tangent);

    if (dot < 0.0) {
      const movementDelta = movement.startPosition.y - movement.endPosition.y;
      if (
        (isCameraUnderground && movementDelta < 0.0) ||
        (!isCameraUnderground && movementDelta > 0.0)
      ) {
        //prevent camera from flipping past the up axis
        constrainedAxis = undefined;
      }

      const oldConstrainedAxis = camera.constrainedAxis;
      camera.constrainedAxis = undefined;
      this.rotate(movement, constrainedAxis, true, false);
      camera.constrainedAxis = oldConstrainedAxis;
    } else {
      this.rotate(movement, constrainedAxis, true, false);
    }

    //now perform the horizontal orbit rotation
    this.setTransform(camera, transform);
    this.rotate(movement, constrainedAxis, false, true);

    if (defined(camera.constrainedAxis)) {
      const right = Cartesian3.cross(
        camera.direction,
        camera.constrainedAxis,
        this._scratchCartesian3,
      );
      if (
        !Cartesian3.equalsEpsilon(right, Cartesian3.ZERO, CesiumMath.EPSILON6)
      ) {
        if (Cartesian3.dot(right, camera.right) < 0.0) {
          Cartesian3.negate(right, right);
        }

        Cartesian3.cross(right, camera.direction, camera.up);
        Cartesian3.cross(camera.direction, camera.up, camera.right);

        Cartesian3.normalize(camera.up, camera.up);
        Cartesian3.normalize(camera.right, camera.right);
      }
    }

    //keep the camera from "popping" or flipping when switching between the two pivot
    //spaces used during the vertical and horizontal orbit steps
    this.setTransform(camera, oldTransform);

    const originalPosition = Cartesian3.clone(
      camera.positionWC,
      this._scratchCartesian3,
    );

    if (!Cartesian3.equals(camera.positionWC, originalPosition)) {
      this.setTransform(camera, verticalTransform);
      camera.worldToCameraCoordinatesPoint(originalPosition, originalPosition);

      const magSquared = Cartesian3.magnitudeSquared(originalPosition);
      if (Cartesian3.magnitudeSquared(camera.position) > magSquared) {
        Cartesian3.normalize(camera.position, camera.position);
        Cartesian3.multiplyByScalar(
          camera.position,
          Math.sqrt(magSquared),
          camera.position,
        );
      }

      const angle = Cartesian3.angleBetween(originalPosition, camera.position);
      const axis = Cartesian3.cross(
        originalPosition,
        camera.position,
        originalPosition,
      );
      Cartesian3.normalize(axis, axis);

      const quaternion = Quaternion.fromAxisAngle(
        axis,
        angle,
        this._scratchQuaternion,
      );
      const rotation = Matrix3.fromQuaternion(quaternion, this._scratchMatrix);
      Matrix3.multiplyByVector(rotation, camera.direction, camera.direction);
      Matrix3.multiplyByVector(rotation, camera.up, camera.up);
      Cartesian3.cross(camera.direction, camera.up, camera.right);
      Cartesian3.cross(camera.right, camera.direction, camera.up);

      this.setTransform(camera, oldTransform);
    }
  }

  /**
   * Part of the orbit algorithm based on Cesium's built-in camera functions.
   * @link https://github.com/CesiumGS/cesium/blob/main/packages/engine/Source/Scene/ScreenSpaceCameraController.js#L2409
   */
  private rotate(
    movement: { startPosition: Cartesian2; endPosition: Cartesian2 },
    constrainedAxis: Cartesian3 | null | undefined,
    rotateOnlyVertical: boolean,
    rotateOnlyHorizontal: boolean,
  ): void {
    const scene = this._viewer.scene;

    const rotateFactor = 1.0;
    const rotateRateRangeAdjustment = 1.0;

    const oldAxis = scene.camera.constrainedAxis;
    if (defined(constrainedAxis)) {
      scene.camera.constrainedAxis = constrainedAxis;
    }

    const rho = Cartesian3.magnitude(scene.camera.position);
    let rotateRate = rotateFactor * (rho - rotateRateRangeAdjustment);
    if (rotateRate > this._maximumRotateRate)
      rotateRate = this._maximumRotateRate;
    if (rotateRate < this._minimumRotateRate)
      rotateRate = this._minimumRotateRate;

    let phiWindowRatio =
      (movement.startPosition.x - movement.endPosition.x) /
      scene.canvas.clientWidth;
    let thetaWindowRatio =
      (movement.startPosition.y - movement.endPosition.y) /
      scene.canvas.clientHeight;
    phiWindowRatio = Math.min(phiWindowRatio, this.maximumMovementRatio);
    thetaWindowRatio = Math.min(thetaWindowRatio, this.maximumMovementRatio);

    const deltaPhi = rotateRate * phiWindowRatio * Math.PI * 2.0;
    let deltaTheta = rotateRate * thetaWindowRatio * Math.PI;

    if (defined(constrainedAxis) && defined(this.maximumTiltAngle)) {
      const maximumTiltAngle = this.maximumTiltAngle;
      const dotProduct = Cartesian3.dot(
        scene.camera.direction,
        constrainedAxis,
      );
      const tilt = Math.PI - Math.acos(dotProduct) + deltaTheta;
      if (tilt > maximumTiltAngle) {
        deltaTheta -= tilt - maximumTiltAngle;
      }
    }

    if (!rotateOnlyVertical) scene.camera.rotateRight(deltaPhi);
    if (!rotateOnlyHorizontal) scene.camera.rotateUp(deltaTheta);

    scene.camera.constrainedAxis = oldAxis;
  }

  private setTransform(camera: Camera, matrix: Matrix4): void {
    camera["_setTransform"](matrix);
  }
}
